import { ChannelkartNetworkGet, ChannelkartNetworkPost, ChannelkartNetworkPut } from "../../services/networkService";
import { SalesRep, Supervisor, Request, RequestType } from "./types";
import { getUserFromToken } from "../../services/userService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { getMetaDataConfig } from "../../services/uiConfigService";
import { EnvironmentRegistry } from "../../services/registry/environmentRegistry";

const env = EnvironmentRegistry.getEnvironment();


export const fetchSupervisors = async (): Promise<Supervisor[]> => {
  try {
    const loginId = getUserLoginId();

    const [response, collectionResp] = await Promise.allSettled([
      ChannelkartNetworkGet(
        `${await getBaseUrl()}/api/v1/approvals/inbox/rfc/supervisors?userId=${loginId}`,
        undefined,
        { ENV: env ?? "" }
      ),
      ChannelkartNetworkGet(
        `${await getBaseUrl()}/api/v1/collections/inbox/rfc/supervisors?userId=${loginId}`,
        undefined,
        { ENV: env ?? "" }
      )
    ]);

    const usersResponse = await ChannelkartNetworkGet(`/users?size=100&filter=designation:supervisor`);

    const data1 =
      response.status === "fulfilled" &&
      response.value.status === 200 &&
      Array.isArray(response.value.data)
        ? response.value.data
        : [];

    const data2 =
      collectionResp.status === "fulfilled" &&
      collectionResp.value.status === 200 &&
      Array.isArray(collectionResp.value.data)
        ? collectionResp.value.data
        : [];

    if (usersResponse.status !== 200 || !Array.isArray(usersResponse.data?.features)) {
      console.warn("Unexpected users response:", usersResponse);
      return [];
    }

    const usersList = usersResponse.data.features;

    const mergedSupervisors = [...data1, ...data2];

    const uniqueMap = new Map<string, any>();

    mergedSupervisors.forEach((sup: any) => {
      const id = sup.supervisorId ?? sup.supervisor_id;
      if (!uniqueMap.has(id)) {
        uniqueMap.set(id, sup);
      } else {
        const existing = uniqueMap.get(id);
        uniqueMap.set(id, {
          ...existing,
        });
      }
    });

    const uniqueSupervisors = Array.from(uniqueMap.values());

    const finalList: Supervisor[] = uniqueSupervisors.map((sup: any) => {
      const matchedUser = usersList.find(
        (u: any) => ((u.loginId === sup.supervisorId) || (u.loginId === sup.supervisor_id))
      );

      return {
        id: sup.supervisorId ?? sup.supervisor_id,
        name: matchedUser?.name ?? "",
        mobile: matchedUser?.mobile ?? "",
        salesRepsCount: sup.salesRepCount ?? "0",
      };
    });

    return finalList;
  } catch (error) {
    console.error("Error fetching supervisors:", error);
    return [];
  }
};

export const fetchSalesreps = async (supervisorId: string): Promise<SalesRep[]> => {
  try {
    const loginId = getUserLoginId();
    const usersResponse = await ChannelkartNetworkGet(`/users?size=700&filter=designation:salesrep`);

    const [response, collectionResp] = await Promise.allSettled([
      ChannelkartNetworkGet(
        `${await getBaseUrl()}/api/v1/approvals/inbox/rfc/supervisors/${supervisorId}/salesreps?userId=${loginId}`,
        undefined,
        { ENV: env ?? "" }
      ),
      ChannelkartNetworkGet(
        `${await getBaseUrl()}/api/v1/collections/inbox/rfc/supervisors/${supervisorId}/collections?userId=${loginId}`,
        undefined,
        { ENV: env ?? "" }
      )
    ]);

    const data1 =
      response.status === "fulfilled" &&
      response.value.status === 200 &&
      Array.isArray(response.value.data)
        ? response.value.data
        : [];

    const data2 =
      collectionResp.status === "fulfilled" &&
      collectionResp.value.status === 200 &&
      Array.isArray(collectionResp.value.data)
        ? collectionResp.value.data
        : [];


    if (usersResponse.status !== 200 || !Array.isArray(usersResponse.data?.features)) {
      console.warn("Unexpected users response:", usersResponse);
      return [];
    }

    const mergedSalesrep = [...data1, ...data2];

    const uniqueMap = new Map<string, any>();

    mergedSalesrep.forEach((rep: any) => {
      const id = rep.salesRepId ?? rep.login_id;
      if (!id) return; // skip invalid
      if (!uniqueMap.has(id)) {
        uniqueMap.set(id, rep);
      } else {
        const existing = uniqueMap.get(id);
        uniqueMap.set(id, { ...existing, ...rep });
      }
    });

    const uniqueSalesreps = Array.from(uniqueMap.values());

    const usersList = usersResponse.data.features;

    const finalList: SalesRep[] = uniqueSalesreps.map((sup: any) => {
      const matchedUser = usersList.find(
        (u: any) => ((u.loginId === sup.salesRepId) || (u.loginId === sup.login_id))
      );

      return {
        id: sup.salesRepId ?? sup.login_id,
        name: matchedUser?.name ?? "",
        mobile: matchedUser?.mobile ?? "",
      };
    });

    return finalList;
  } catch (error) {
    console.error("Error fetching supervisors:", error);
    return [];
  }
};

export const fetchRequests = async (
  supervisorId: string,
  salesrepId: string
): Promise<Request[]> => {
  try {
    const loginId = getUserLoginId();
    const [response, collectionResp] = await Promise.allSettled([
      ChannelkartNetworkGet(
        `${await getBaseUrl()}/api/v1/approvals/inbox?role=RFC&userId=${loginId}&salesRepId=${salesrepId}`,
        undefined,
        { ENV: env ?? "" }
      ),
      ChannelkartNetworkGet(
        `${await getBaseUrl()}/api/v1/collections/payment-requests/sales-rep/${salesrepId}`,
        undefined,
        { ENV: env ?? "" }
      ),
    ]);

    const data1 =
      response.status === "fulfilled" &&
      response.value.status === 200 &&
      Array.isArray(response.value.data)
        ? response.value.data
        : [];


    const data2 =
      collectionResp.status === "fulfilled" &&
        collectionResp.value.status === 200 &&
        Array.isArray(collectionResp.value.data)
        ? collectionResp.value.data.map((item: any) => ({
          ...item,
          request_type: "COLLECTION",
        }))
        : [];

    if (data1.length === 0 && data2.length === 0) {
      console.warn("Both API calls failed. Using mock data.");
      return [];
    }

    const apiData = [...data1, ...data2];

    const transformed: Request[] = apiData.map((item: any) => {
      let type: Request["type"] = "order-taking";
      let data: any;

      const reqType = item.request_type?.toUpperCase() || "";

      if (reqType.startsWith("INCREASE")) {
        type = "credit-limit";
        data = {
          creditType: item.credit_type?.toUpperCase() || "",
          amount: item.requested_amount?.toString() ?? "0",
          days: item.requested_days?.toString() ?? "0",
          invoice: item.requested_invoices?.toString() ?? "0",
        };
      } else {
        switch (reqType) {
          case "BYPASS":
            type = "order-bypass";
            data = {
              amount: item.requested_amount?.toString() ?? "0",
              days: item.requested_days?.toString() ?? "0",
              invoice: item.requested_invoices?.toString() ?? "0",
            };
            break;

          case "COLLECTION":
            type = "collection";

            const paymentMode = item.mode_of_payment?.toLowerCase() || "";

            switch (paymentMode) {
              case "cash":
                data = {
                  modeOfPayment: "cash",
                  cash: item.collected_amount ?? 0,
                };
                break;

              case "cheque":
                data = {
                  modeOfPayment: "cheque",
                  chequeNumber: item.cheque_no ?? "",
                  amount: item.collected_amount ?? 0,
                  bankName: item.bank_name ?? "",
                  paymentProofImage: item.blob_key ?? "",
                };
                break;

              case "bank_transfer":
              case "upi":
                data = {
                  modeOfPayment: paymentMode,
                  iban: item.international_bank_account_number ?? "",
                  accountNo: item.bank_account_number ?? "",
                  transactionId: item.transaction_id ?? "",
                  bankTransfer: item.collected_amount ?? 0,
                  paymentProofImage: item.blob_key ?? "",
                };
                break;

              case "pos":
                data = {
                  modeOfPayment: "pos",
                  amount: item.collected_amount ?? 0,
                  transactionId: item.transaction_id ?? "",
                };
                break;

              case "e_wallet":
                data = {
                  modeOfPayment: "e_wallet",
                  walletNumber: item.wallet_number ?? "",
                  provider: item.provider ?? "",
                  amount: item.collected_amount ?? 0,
                  transactionId: item.transaction_id ?? "",
                };
                break;

              default:
                data = {
                  mode: "Unknown",
                  amount: item.total_amount ?? 0,
                };
                break;
            }
            break;

          case "UNBLOCK":
          default:
            type = "order-taking";
            data = {
              orderNo: item.order_number,
              orderAmt: item.requested_amount,
            };
            break;

        }
      }

      return {
        id: item.approval_id ?? item.id ?? item.request_id ?? "",
        type,
        storeName: item.outlet_info?.outlet_name ?? item.outlet_name ?? "",
        storeId: item.outlet_code ?? item.outletcode ?? "",
        salesRepId: item.requested_by ?? "",
        currentCreditLimit: `${
          (item.outlet_info?.current_credit_limit ??
            item.total_credit ??
            "0")?.toString()
        }`,
        availCreditLimit: `${
          (item.outlet_info?.available_credit ??
            item.available_credit ??
            "0")?.toString()
        }`,
        data,
        checked: false,
        status: item.status ?? "",
        outletType: item.outlet_type,
        ...(type === "collection"
          ? {
            invoiceNumber: item.invoice_number ?? "",
            orderDate: item.order_date
              ? new Date(item.order_date).toISOString().split("T")[0]
              : "",
          }
          : {}),
      };
    });

    transformed.sort((a, b) => {
      const nameA = a.storeName || "";
      const nameB = b.storeName || "";

      const startsWithLetter = (s: string) => /^[A-Za-z]/.test(s);

      const aIsLetter = startsWithLetter(nameA);
      const bIsLetter = startsWithLetter(nameB);

      if (aIsLetter && !bIsLetter) return -1;

      if (!aIsLetter && bIsLetter) return 1;

      return nameA.localeCompare(nameB);
    });

    return transformed;
  } catch (error) {
    console.error("Error fetching supervisors:", error);
    return [];
  }
};


export const approveRequest = async (approvalId: string, type: string, data: string, salesrepId: string) => {
  const loginId = getUserLoginId();
  try {
    if (type == "order-bypass") {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;

      const amount = parsedData?.amount ?? "0";
      const days = parsedData?.days ?? "0";
      const invoice = parsedData?.invoice ?? "0";

      if (
        (amount === "0" || amount === "" || amount === null) &&
        (days === "0" || days === "" || days === null) &&
        (invoice === "0" || invoice === "" || invoice === null)
      ) {
        return "invalid";
      }

      const reqBody = {
        actionBy: loginId,
        approvedAmount: Number(parseFloat(amount).toFixed(2)), 
        approvedInvoices: parseInt(invoice, 10) || 0,
        approvedDays: parseInt(days, 10) || 0,
      };

      const response: any = await ChannelkartNetworkPost(`${await getBaseUrl()}/api/v1/approvals/bypass/rfc/${approvalId}/approve`, reqBody, { ENV: env ?? "" });


      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return response.data.status.toString().toLowerCase();

    } else if (type == "order-taking") {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const reqBody = {
        "actionBy": "rfc_manager"
      }
      const response: any = await ChannelkartNetworkPost(`${await getBaseUrl()}/api/v1/approvals/inbox/rfc/orders/${parsedData.orderNo}/approve?loginId=${salesrepId}&userId=${loginId}`, reqBody, { ENV: env ?? "" });

      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return response.data.creditStatus.toString().toLowerCase();

    } else if (type == "collection") {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const reqBody = {
        "paymentMethod": parsedData.modeOfPayment.toUpperCase(),
        "outletType": "Credit",
        "idList": [
          approvalId
        ]
      }
      const response: any = await ChannelkartNetworkPut(`/v1/paymentCollection/approvePaymentCollection`, reqBody, { ENV: env ?? "" });

      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return "approved";

    } else {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;

      const amount = parsedData?.amount ?? "0";
      const days = parsedData?.days ?? "0";
      const invoice = parsedData?.invoice ?? "0";

      if (
        (amount === "0" || amount === "" || amount === null) &&
        (days === "0" || days === "" || days === null) &&
        (invoice === "0" || invoice === "" || invoice === null)
      ) {
        return "invalid";
      }

      const reqBody = {
        "actionBy": "rfc_manager",
        approvedAmount: Number(parseFloat(amount).toFixed(2)), 
        approvedInvoices: parseInt(invoice, 10) || 0,
        approvedDays: parseInt(days, 10) || 0,
      }

      const response: any = await ChannelkartNetworkPost(`${await getBaseUrl()}/api/v1/approvals/${approvalId}/rfc-approve`, reqBody, { ENV: env ?? "" });

      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return response.data.status.toString().toLowerCase();
    }
  } catch (e) {
    return "pending";
  }
}

export const rejectRequest = async (approvalId: string, type: string, data: string, salesrepId: string) => {
  const loginId = getUserLoginId();
  try {
    if (type == "order-bypass") {

      const reqBody = {
        actionBy: loginId,
      };

      const response: any = await ChannelkartNetworkPost(`${await getBaseUrl()}/api/v1/approvals/bypass/rfc/${approvalId}/reject`, reqBody, { ENV: env ?? "" });


      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return response.data.status.toString().toLowerCase();

    } else if (type == "order-taking") {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const reqBody = {
        actionBy: loginId,
      };

      const response: any = await ChannelkartNetworkPost(`${await getBaseUrl()}/api/v1/approvals/inbox/rfc/orders/${parsedData.orderNo}/reject?loginId=${salesrepId}&userId=${loginId}`, reqBody, { ENV: env ?? "" });

      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return response.data.creditStatus.toString().toLowerCase();

    } else if (type == "collection") {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const reqBody = {
        "paymentMethod": parsedData.modeOfPayment.toUpperCase(),
        "outletType": "Credit",
        "idList": [
          approvalId
        ]
      }
      const response: any = await ChannelkartNetworkPut(`/v1/paymentCollection/rejectPaymentCollection`, reqBody, { ENV: env ?? "" });

      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return "rejected";

    } else {
      const reqBody = {
        "actionBy": "rfc_manager"
      }
      const response: any = await ChannelkartNetworkPost(`${await getBaseUrl()}/api/v1/approvals/${approvalId}/reject`, reqBody, { ENV: env ?? "" });

      if (response.status !== 200) {
        console.warn("Unexpected supervisor response:", response);
        return [];
      }

      return response.data.status.toString().toLowerCase();
    }
  } catch (e) {
    return "pending";
  }
}

const getUserLoginId = () => {
  const authContext = localStorage.getItem('authContext');
  if (authContext) {
    try {
      const authData = JSON.parse(authContext);
      return authData.loginId ?? authData.user?.loginId ?? authData.user?.loginid ?? '';
    } catch (e) {
      console.error('Error parsing auth context:', e);
    }
  }
  return '';
};

const getBaseUrl = async () => {
  const portalConfig = await getMetaDataConfig(
    "clientconfig",
    "portal_configuration"
  );

  const baseUrl = portalConfig?.domainValues?.find(
    (config: any) => config.name === "baseUrl"
  );

  if (baseUrl && baseUrl !== "") return baseUrl;
  return `https://credits-${env}.salescode.ai`;
}

export const defaultLabelMap: Record<RequestType, { text: string; color: string }> = {
  "credit-limit": { text: "Credit Limit", color: "#C68DF9" },
  "collection": { text: "Collection", color: "#F9B27F" },
  "order-taking": { text: "Order Taking", color: "#E2B77E" },
  "order-bypass": { text: "Order Bypass", color: "#8FD2FF" },
};

export const getLabelMap = async (): Promise<
  Record<RequestType, { text: string; color: string }>
> => {
  try {
    const portalConfig = await getMetaDataConfig("clientconfig", "portal_configuration");

    const creditCollectionFilterLabels = portalConfig?.domainValues?.find(
      (config: any) => config.name === "labelcreditCollectionFilterLabels"
    );

    const configList = creditCollectionFilterLabels?.values; 

    if (Array.isArray(configList) && configList.length > 0) {
      const map: Record<string, { text: string; color: string }> = {};

      configList.forEach((item: any) => {
        if (item.id && item.text && item.color) {
          map[item.id] = { text: item.text, color: item.color };
        }
      });

      return { ...defaultLabelMap, ...map } as Record<RequestType, { text: string; color: string }>;
    }

    return defaultLabelMap;
  } catch (error) {
    console.warn("⚠️ Error fetching label map config, using defaults:", error);
    return defaultLabelMap;
  }

};

export const formatAmount = (value: number | string): string => {
  if (value === null || value === undefined || value === "") return "0";

  const sanitized = value.toString().replace(/,/g, "");

  const num = Number(sanitized);
  if (isNaN(num)) return "0";

  const [integerPart, decimalPart] = sanitized.split(".");

  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);
  const formattedInteger =
    (otherNumbers ? otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," : "") + lastThree;

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};


